import React, { useState } from 'react';
import { Upload, X, Image as ImageIcon, Plus, Building } from 'lucide-react';

interface PropertyImage {
  id: string;
  url: string;
  caption: string;
  propertyId: string;
  isMain: boolean;
}

interface PropertyImageManagerProps {
  propertyId?: string;
  images?: PropertyImage[];
  onImageUpload?: (file: File, caption: string, propertyId: string) => void;
  onImageDelete?: (imageId: string) => void;
  onSetMainImage?: (imageId: string, propertyId: string) => void;
}

const PropertyImageManager: React.FC<PropertyImageManagerProps> = ({
  propertyId = 'saratoga',
  images = [],
  onImageUpload,
  onImageDelete,
  onSetMainImage,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [newImageCaption, setNewImageCaption] = useState('');
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(propertyId);

  const propertyOptions = [
    { id: 'saratoga', name: '21 Saratoga' },
    { id: 'highlands', name: 'Highlands Lofts' },
  ];

  const filteredImages = images.filter(img => img.propertyId === selectedProperty);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      alert('Image size must be less than 5MB');
      return;
    }

    setIsUploading(true);
    try {
      if (onImageUpload) {
        await onImageUpload(file, newImageCaption, selectedProperty);
      }
      setNewImageCaption('');
      setShowUploadForm(false);
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <h3 className="text-lg font-semibold text-gray-900">Property Images</h3>
          <select
            value={selectedProperty}
            onChange={(e) => setSelectedProperty(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {propertyOptions.map(option => (
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </select>
        </div>
        <button
          onClick={() => setShowUploadForm(true)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Image
        </button>
      </div>

      {showUploadForm && (
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Property
              </label>
              <select
                value={selectedProperty}
                onChange={(e) => setSelectedProperty(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {propertyOptions.map(option => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Image Caption
              </label>
              <input
                type="text"
                value={newImageCaption}
                onChange={(e) => setNewImageCaption(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter image caption"
              />
            </div>

            <div>
              <label className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <Upload className="w-4 h-4 mr-2 text-gray-600" />
                <span className="text-gray-700">Choose Image</span>
                <input
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={handleFileChange}
                  disabled={isUploading}
                />
              </label>
              <p className="mt-2 text-sm text-gray-500">
                Maximum file size: 5MB. Supported formats: JPG, PNG, GIF
              </p>
            </div>

            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => setShowUploadForm(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredImages.map((image) => (
          <div
            key={image.id}
            className="group relative bg-white rounded-lg shadow-md overflow-hidden"
          >
            <div className="aspect-w-16 aspect-h-9">
              <img
                src={image.url}
                alt={image.caption}
                className="object-cover w-full h-full"
              />
            </div>

            <div className="p-4 space-y-2">
              <p className="text-sm text-gray-600">{image.caption}</p>
              
              <div className="flex items-center justify-between">
                <button
                  onClick={() => onSetMainImage?.(image.id, image.propertyId)}
                  className={`px-3 py-1 rounded-full text-xs ${
                    image.isMain
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800 hover:bg-blue-100 hover:text-blue-800'
                  }`}
                >
                  {image.isMain ? 'Main Image' : 'Set as Main'}
                </button>

                <button
                  onClick={() => onImageDelete?.(image.id)}
                  className="p-1 text-gray-500 hover:text-red-600 transition-colors"
                  title="Delete image"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}

        {filteredImages.length === 0 && (
          <div className="col-span-full flex flex-col items-center justify-center p-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
            <Building className="w-12 h-12 text-gray-400 mb-4" />
            <p className="text-gray-600">No images uploaded for this property</p>
            <button
              onClick={() => setShowUploadForm(true)}
              className="mt-4 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              Upload Images
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PropertyImageManager;